import { MaterialCommunityIcons } from "@expo/vector-icons";
import { mostLikelyIcdSuggestion, sortIcdSuggestionsByLikelihood } from "@taskhealth/shared";
import { useEffect, useMemo, useRef, useState } from "react";
import {
    ActivityIndicator,
    Animated,
    Platform,
    Pressable,
    StyleSheet,
    TextInput,
    TouchableOpacity,
    View
} from "react-native";
import { Text } from "react-native-paper";
import Svg, { Defs, LinearGradient, Path, Stop } from "react-native-svg";

import { IcdChipPicker } from "./IcdChipPicker";
import { SourcePhotoButton } from "./SourcePhotoButton";
import { SourcePhotoViewer } from "./SourcePhotoViewer";
import { StrengthQuestion } from "./StrengthQuestion";
import { SuggestionQuestion, SUPPLEMENT_ROUTES } from "./SuggestionQuestion";

import { ConfirmDialog } from "@/components/ConfirmDialog";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import type { EnrichmentResult } from "@/hooks/useDraftEnrichment";
import { useMedicationFieldSuggestions } from "@/hooks/useMedicationFieldSuggestions";
import { fetchDraftImages } from "@/lib/api";
import type {
    DraftSourceImage,
    MedicationDraft,
    MedicationDraftProductSuggestion
} from "@/lib/api";
import { resolveCatalogStrength } from "@/lib/catalog-strength";
import { HALFTONE_MAX_OPACITY, HALFTONE_MIN_OPACITY, halftoneDotsPath } from "@/lib/halftone";
import { isPrnFrequency, mddChipSuggestions } from "@/lib/mdd-utils";
import { colors, fonts, radius, shadows, spacing } from "@/lib/theme";

// ── Types ──

const MAX_SUGGESTION_CHIPS = 3;

interface IcdCode {
    code: string;
    description: string;
    likelihood?: number;
}

export interface CardSelections {
    name: string;
    route: string | null;
    isSupplement: boolean;
    strength: string | null;
    strengthNotApplicable?: boolean;
    dose: string | null;
    frequency: string | null;
    adminNotes: string;
    maxDailyDose: string | null;
    icdCodes: IcdCode[];
    isComplete: boolean;
}

interface Props {
    draft: MedicationDraft;
    enrichment: EnrichmentResult;
    onSaved: (draftId: string) => Promise<boolean>;
    onDiscard: () => void;
    onSelectionChange: (sel: CardSelections) => void;
    saving?: boolean;
    /**
     * The extraction job that produced this draft is still running. Diagnosis
     * codes are filled in server-side AFTER the card is broadcast, so a card can
     * be on screen and complete-looking while its codes are still coming.
     */
    extractionInProgress?: boolean;
    /**
     * The extraction job currently running on this visit, if any. A card is only
     * "still linking" while ITS OWN job is the one running — otherwise starting a
     * dictation would put every already-verified photo card back into a spinner.
     */
    activeExtractionJobId?: string | null;
}

// ── Helpers ──

function buildAdminNotes(
    selectedNotes: Set<string>,
    customNote: string,
    draftNotes: string | null
): string {
    const parts = [...selectedNotes];
    if (customNote.trim()) {
        parts.push(customNote.trim());
    }
    if (draftNotes && parts.length === 0) {
        return draftNotes;
    }
    return parts.join(", ");
}

function normalizedChipKey(value: string): string {
    return value
        .toLowerCase()
        .replace(/\btablets\b/g, "tablet")
        .replace(/\s+/g, " ")
        .trim();
}

function normalizeSuggestionLabel(value: string): string {
    return value.replace(/\bevery\s+46\s+hours\b/gi, "Every 4-6 hours").trim();
}

function mergeStringOptions(...groups: (string | null | undefined | string[])[]): string[] {
    const values: string[] = [];
    for (const group of groups) {
        const items = Array.isArray(group) ? group : [group];
        for (const item of items) {
            const trimmed = item ? normalizeSuggestionLabel(item) : null;
            if (!trimmed) {
                continue;
            }
            const key = normalizedChipKey(trimmed);
            if (!values.some((existing) => normalizedChipKey(existing) === key)) {
                values.push(trimmed);
            }
        }
    }
    return values.slice(0, MAX_SUGGESTION_CHIPS);
}

function normalizationStatus(mergeNotes?: string | null): string | null {
    return /normalization status ([a-z_]+)/i.exec(mergeNotes ?? "")?.[1]?.toLowerCase() ?? null;
}

function hasLocalNormalization(mergeNotes?: string | null): boolean {
    return /Normalized by local medication DB/i.test(mergeNotes ?? "");
}

function hasRawExtractionMarker(mergeNotes: string | null | undefined, marker: string): boolean {
    return new RegExp(`Raw ${marker}:`, "i").test(mergeNotes ?? "");
}

function extractedDraftValue(
    value: string | null,
    mergeNotes: string | null | undefined,
    rawMarker: string
): string | null {
    if (!value || !hasLocalNormalization(mergeNotes)) {
        return value;
    }
    return hasRawExtractionMarker(mergeNotes, rawMarker) ? value : null;
}

function removeOptionLabelFromName(
    name: string,
    option: MedicationDraftProductSuggestion | undefined
): string {
    if (!option?.label) {
        return name;
    }
    const suffix = ` - ${option.label}`;
    return name.endsWith(suffix) ? name.slice(0, -suffix.length) : name;
}

/**
 * Halftone fade, the way a car-window sticker dissolves into the glass: a grid of
 * dots that grows and darkens left to right, so the header reads as "still being
 * worked on" without a spinner filling the whole bar.
 *
 * Drawn rather than animated on purpose — this state can sit on screen for ten
 * seconds or more, and a looping animation on every unfinished card in a long
 * list is a lot of movement for something the nurse cannot act on.
 *
 * Drawn as ONE path, not one <Circle> per dot. Each Circle is a native view, and
 * a photo run streams every card onto the hub in this state at once — that was
 * several thousand views mounted in a second, then rasterised by LogRocket on
 * every capture, and the UI thread stalled for seconds at a time
 * (TASKHEALTH-MOBILE-3Y). The opacity ramp the circles carried individually is
 * now a gradient fill; the radius ramp lives in the path (lib/halftone.ts).
 */
function ProcessingFade({ width, height }: { width: number; height: number }) {
    const { d } = useMemo(() => halftoneDotsPath(width, height), [width, height]);
    if (!d) {
        return null;
    }
    return (
        <Svg
            width={width}
            height={height}
            viewBox={`0 0 ${width} ${height}`}
            style={ss.processingFade}
            pointerEvents="none"
        >
            <Defs>
                {/* objectBoundingBox units: 0 → the first dot column, 1 → the right edge,
                    which is exactly the span the old per-column opacity ramp covered. */}
                <LinearGradient id="halftoneFade" x1="0" y1="0" x2="1" y2="0">
                    <Stop
                        offset="0"
                        stopColor={colors.gray400}
                        stopOpacity={HALFTONE_MIN_OPACITY}
                    />
                    <Stop
                        offset="1"
                        stopColor={colors.gray400}
                        stopOpacity={HALFTONE_MAX_OPACITY}
                    />
                </LinearGradient>
            </Defs>
            <Path d={d} fill="url(#halftoneFade)" />
        </Svg>
    );
}

// ── Component ──

export function DraftCard({
    draft,
    enrichment,
    onSaved,
    onDiscard,
    onSelectionChange,
    saving: externalSaving,
    extractionInProgress = false,
    activeExtractionJobId = null
}: Props) {
    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();
    const isSupplement = draft.isSupplement ?? false;
    // Ranked here as well as on the server so a draft row written before the
    // likelihood field existed, or one that reached the phone unsorted, still
    // pre-selects the most likely code rather than whichever came first.
    const extractionIcds = sortIcdSuggestionsByLikelihood(draft.suggestedIcdCodes ?? []);
    const productOptions = draft.normalizationSuggestions?.medicationOptions ?? [];
    const status = normalizationStatus(draft.mergeNotes);
    const needsMedicationConfirmation =
        !isSupplement &&
        productOptions.length > 0 &&
        ["options_strength_missing", "check_strength_mismatch", "needs_review"].includes(
            status ?? ""
        );
    const extractedDraftDose = extractedDraftValue(
        draft.orderedDose,
        draft.mergeNotes,
        "ordered dose"
    );
    const extractedDraftFrequency = extractedDraftValue(
        draft.frequency,
        draft.mergeNotes,
        "frequency"
    );
    const extractedDraftAdministrationNotes = extractedDraftValue(
        draft.administrationNotes,
        draft.mergeNotes,
        "administration notes"
    );
    const cardIsEnriching = enrichment.loading && !isSupplement;
    // Codes are written to the draft row by enrichDraftIcdCodes AFTER the draft
    // is broadcast, so between the card appearing and the job completing there is
    // a window where it looks incomplete but nothing is waiting on the nurse.
    const awaitingDiagnoses =
        !isSupplement &&
        extractionIcds.length === 0 &&
        (enrichment.suggestions?.icdCodes?.length ?? 0) === 0 &&
        (extractionInProgress || cardIsEnriching);
    // The indexes on the card during a run are the EXTRACTOR's, and it gets them
    // wrong often enough that opening one mid-run can show the wrong bottle. A
    // second pass re-derives them server-side and the corrected set arrives with
    // the completion refetch, so until the job finishes the button says "working"
    // rather than offering a link that may move.
    // Gated on the card's own job: a draft from a finished photo run keeps its
    // magnifier while a dictation runs alongside it. A draft with no job id
    // predates the column and can never be mid-link.
    const awaitingImageLinks =
        extractionInProgress &&
        draft.jobId != null &&
        activeExtractionJobId != null &&
        String(draft.jobId) === activeExtractionJobId;
    // The pre-selected chip is the single most likely code. Extraction codes
    // win over the enrichment fallback when both exist (they are the same
    // suggestion, just delivered on the row instead of by a second call).
    const topIcdCode =
        (!isSupplement && mostLikelyIcdSuggestion(extractionIcds)?.code) ||
        (!isSupplement && mostLikelyIcdSuggestion(enrichment.suggestions?.icdCodes ?? [])?.code) ||
        null;

    // ── Selection state ──

    const [medicationOptionConfirmed, setMedicationOptionConfirmed] = useState(
        !needsMedicationConfirmation
    );
    const [selectedMedicationName, setSelectedMedicationName] = useState<string>(
        draft.medicationName ?? draft.name
    );
    const [selectedRoute, setSelectedRoute] = useState<string | null>(draft.route ?? null);
    const [selectedDose, setSelectedDose] = useState<string | null>(extractedDraftDose ?? null);
    const [selectedFreq, setSelectedFreq] = useState<string | null>(
        extractedDraftFrequency ?? null
    );
    const [selectedNotes, setSelectedNotes] = useState<Set<string>>(
        extractedDraftAdministrationNotes ? new Set([extractedDraftAdministrationNotes]) : new Set()
    );
    const [selectedIcds, setSelectedIcds] = useState<Set<string>>(
        () => new Set(topIcdCode ? [topIcdCode] : [])
    );
    const [addedIcds, setAddedIcds] = useState<IcdCode[]>([]);

    const [customDose, setCustomDose] = useState("");
    const [customFreq, setCustomFreq] = useState("");
    const [showCustomFreq, setShowCustomFreq] = useState(false);
    const [showCustomDose, setShowCustomDose] = useState(false);
    const [customNote, setCustomNote] = useState("");
    const [showCustomNote, setShowCustomNote] = useState(false);
    // MDD is its OWN field (never the dose): the explicitly stated ceiling
    // from the extraction comes preselected; the schedule-derived value is
    // offered as another chip; Other takes free text.
    const [selectedMdd, setSelectedMdd] = useState<string | null>(draft.maxDailyDose ?? null);
    const [customMdd, setCustomMdd] = useState("");
    const [showCustomMdd, setShowCustomMdd] = useState(false);

    const [enteredStrength, setEnteredStrength] = useState("");
    const [strengthNotApplicable, setStrengthNotApplicable] = useState(false);
    const [showErrors, setShowErrors] = useState(false);
    // Cards start OPEN. Collapsing is per-card and deliberately not persisted:
    // a nurse works down the list once, and a card that reopened folded after a
    // refetch would read as work she had already done.
    const [collapsed, setCollapsed] = useState(false);
    // The halftone is drawn at the header's real size so the dots stay circular —
    // stretching one fixed viewBox to fit would turn them into ovals, and the
    // header's height changes with the "Please fill in …" line.
    const [headerSize, setHeaderSize] = useState({ width: 0, height: 0 });
    // Source photos, fetched only when asked for: the URLs are signed and short-
    // lived, so pre-loading them for every card would mostly waste expired links.
    const sourceImageIndexes = draft.sourceImageIndexes ?? [];
    const [imagesOpen, setImagesOpen] = useState(false);
    const [images, setImages] = useState<DraftSourceImage[]>([]);
    const [imagesLoading, setImagesLoading] = useState(false);
    const [imagesError, setImagesError] = useState<string | null>(null);
    const [animatingOut, setAnimatingOut] = useState(false);

    useEffect(() => {
        setEnteredStrength("");
        setStrengthNotApplicable(false);
        setMedicationOptionConfirmed(!needsMedicationConfirmation);
        setSelectedMedicationName(draft.medicationName ?? draft.name);
        setSelectedRoute(draft.route ?? null);
        setSelectedDose(extractedDraftDose ?? null);
        setSelectedFreq(extractedDraftFrequency ?? null);
        setSelectedNotes(
            extractedDraftAdministrationNotes
                ? new Set([extractedDraftAdministrationNotes])
                : new Set()
        );
        setSelectedIcds(new Set(topIcdCode ? [topIcdCode] : []));
        setAddedIcds([]);
        setCustomDose("");
        setCustomFreq("");
        setShowCustomFreq(false);
        setShowCustomDose(false);
        setCustomNote("");
        setShowCustomNote(false);
        setSelectedMdd(draft.maxDailyDose ?? null);
        setCustomMdd("");
        setShowCustomMdd(false);
        setShowErrors(false);
    }, [
        draft.id,
        draft.medicationName,
        draft.name,
        draft.route,
        needsMedicationConfirmation,
        extractedDraftDose,
        extractedDraftFrequency,
        extractedDraftAdministrationNotes
    ]);

    useEffect(() => {
        if (!topIcdCode) {
            return;
        }
        setSelectedIcds((prev) => (prev.size > 0 ? prev : new Set([topIcdCode])));
    }, [topIcdCode]);

    // ── Animations ──

    const shakeAnim = useRef(new Animated.Value(0)).current;
    const slideAnim = useRef(new Animated.Value(0)).current;
    const opacityAnim = useRef(new Animated.Value(1)).current;

    const USE_NATIVE = Platform.OS !== "web";

    const triggerShake = () => {
        Animated.sequence([
            Animated.timing(shakeAnim, { toValue: 3, duration: 50, useNativeDriver: USE_NATIVE }),
            Animated.timing(shakeAnim, { toValue: -3, duration: 50, useNativeDriver: USE_NATIVE }),
            Animated.timing(shakeAnim, { toValue: 3, duration: 50, useNativeDriver: USE_NATIVE }),
            Animated.timing(shakeAnim, { toValue: -3, duration: 50, useNativeDriver: USE_NATIVE }),
            Animated.timing(shakeAnim, { toValue: 2, duration: 50, useNativeDriver: USE_NATIVE }),
            Animated.timing(shakeAnim, { toValue: 0, duration: 50, useNativeDriver: USE_NATIVE })
        ]).start();
    };

    const heightAnim = useRef(new Animated.Value(1)).current;
    const marginAnim = useRef(new Animated.Value(14)).current;
    const triggerSlideOut = (cb: () => void) => {
        setAnimatingOut(true);
        // Phase 1: slide right + fade (400ms)
        Animated.parallel([
            Animated.timing(slideAnim, {
                toValue: 100,
                duration: 400,
                useNativeDriver: USE_NATIVE
            }),
            Animated.timing(opacityAnim, {
                toValue: 0,
                duration: 350,
                useNativeDriver: USE_NATIVE
            })
        ]).start(() => {
            // Phase 2: collapse height (250ms) — can't use native driver for layout
            Animated.parallel([
                Animated.timing(heightAnim, {
                    toValue: 0,
                    duration: 250,
                    useNativeDriver: false
                }),
                Animated.timing(marginAnim, {
                    toValue: 0,
                    duration: 250,
                    useNativeDriver: false
                })
            ]).start(() => {
                cb();
            });
        });
    };

    // ── Derived values ──

    const selectedProductOption =
        productOptions.find((option) => option.name === selectedMedicationName) ??
        productOptions.find((option) => option.name === (draft.medicationName ?? draft.name)) ??
        productOptions[0];
    const selectedStrength =
        selectedProductOption?.strength ??
        selectedProductOption?.strengthForm ??
        draft.strength ??
        draft.dosage ??
        null;
    const resolvedStrength = resolveCatalogStrength({
        catalogStrength: selectedStrength,
        enteredStrength,
        notApplicable: strengthNotApplicable,
        isSupplement
    });
    const headerTitle =
        needsMedicationConfirmation && !medicationOptionConfirmed
            ? removeOptionLabelFromName(selectedMedicationName, selectedProductOption)
            : selectedMedicationName;
    const supplementSuggestions = useMedicationFieldSuggestions(
        draft.visitId,
        selectedMedicationName,
        ["doses", "frequency", "administrationNotes"],
        isSupplement
    );
    const doseSuggestions = mergeStringOptions(
        selectedProductOption?.doses,
        draft.normalizationSuggestions?.doses,
        enrichment.suggestions?.doses,
        supplementSuggestions.suggestions?.doses
    );
    const frequencySuggestions = mergeStringOptions(
        selectedProductOption?.frequencies,
        draft.normalizationSuggestions?.frequencies,
        enrichment.suggestions?.frequencies,
        supplementSuggestions.suggestions?.frequencies
    );
    const adminNoteSuggestions = mergeStringOptions(
        selectedProductOption?.administrationNotes,
        draft.normalizationSuggestions?.administrationNotes,
        enrichment.suggestions?.administrationNotes,
        supplementSuggestions.suggestions?.administrationNotes
    );
    const adminNoteOptions = mergeStringOptions(
        extractedDraftAdministrationNotes,
        draft.administrationNotes,
        adminNoteSuggestions
    );
    const needsAdminNoteChoice =
        !extractedDraftAdministrationNotes &&
        adminNoteOptions.length > 0 &&
        selectedNotes.size === 0 &&
        !customNote.trim() &&
        !enrichment.loading;
    const effectiveDose =
        (selectedDose ?? (customDose.trim() || null) ?? extractedDraftDose)?.trim() || null;
    const effectiveFreq =
        (selectedFreq ?? (customFreq.trim() || null) ?? extractedDraftFrequency)?.trim() || null;

    // ── PRN: MDD is asked as its own field below the frequency ──
    const askMdd = isPrnFrequency(effectiveFreq);
    const mddSuggestions = askMdd ? mddChipSuggestions(effectiveDose, effectiveFreq) : [];
    const mddExplainByValue = new Map(mddSuggestions.map((sg) => [sg.value, sg.explain]));
    const mddChipOptions = [draft.maxDailyDose, ...mddSuggestions.map((sg) => sg.value)]
        .filter((v): v is string => !!v && !!v.trim())
        .filter((v, i, arr) => arr.findIndex((x) => x.toLowerCase() === v.toLowerCase()) === i);
    const effectiveMdd = showCustomMdd ? customMdd.trim() || null : selectedMdd;

    const isComplete = !!(
        !cardIsEnriching &&
        (!needsMedicationConfirmation || medicationOptionConfirmed) &&
        resolvedStrength.ready &&
        effectiveDose &&
        effectiveFreq &&
        (isSupplement
            ? selectedRoute?.trim()
            : (!askMdd || !!effectiveMdd) && selectedIcds.size > 0)
    );

    // The single source of truth for "what is this card still waiting on".
    // Both the save guard and the header status chip read this, so the amber
    // dot can never disagree with what Save refuses to accept.
    const missingFields: string[] = [];
    if (!cardIsEnriching) {
        if (!resolvedStrength.ready) {
            missingFields.push("strength");
        }
        if (isSupplement && !selectedRoute?.trim()) {
            missingFields.push("route");
        }
        if (needsMedicationConfirmation && !medicationOptionConfirmed) {
            missingFields.push("medication option");
        }
        if (!effectiveDose) {
            missingFields.push("dose");
        }
        if (!isSupplement && askMdd && !effectiveMdd) {
            missingFields.push("max daily dose");
        }
        if (!effectiveFreq) {
            missingFields.push("frequency");
        }
        // Not listed while the server is still writing codes in: it is not
        // something she can act on, and "needs a diagnosis code" next to a
        // spinner reads as an error rather than as work in flight.
        if (!isSupplement && selectedIcds.size === 0 && !awaitingDiagnoses) {
            missingFields.push("at least one diagnosis code");
        }
    }

    // All ICD codes: extraction + suggestions + user-added
    const suggestionIcds = sortIcdSuggestionsByLikelihood(enrichment.suggestions?.icdCodes ?? []);
    const allIcdCodes = [
        ...extractionIcds,
        ...suggestionIcds.filter((s) => !extractionIcds.some((e) => e.code === s.code)),
        ...addedIcds.filter(
            (a) =>
                !extractionIcds.some((e) => e.code === a.code) &&
                !suggestionIcds.some((s) => s.code === a.code)
        )
    ];
    const visibleIcdCodes = allIcdCodes.slice(0, MAX_SUGGESTION_CHIPS);

    // ── Report selections to parent ──

    const prevCompleteRef = useRef(isComplete);
    useEffect(() => {
        const sel: CardSelections = {
            name: selectedMedicationName,
            route: selectedRoute,
            isSupplement,
            strength: resolvedStrength.strength ?? null,
            strengthNotApplicable: resolvedStrength.needsEntry && strengthNotApplicable,
            dose: effectiveDose ?? null,
            maxDailyDose: askMdd ? effectiveMdd : null,
            frequency: effectiveFreq,
            adminNotes: buildAdminNotes(
                selectedNotes,
                customNote,
                extractedDraftAdministrationNotes
            ),
            icdCodes: allIcdCodes.filter((c) => selectedIcds.has(c.code)),
            isComplete
        };
        onSelectionChange(sel);

        // Only notify parent's completeCount when isComplete flips
        prevCompleteRef.current = isComplete;
    }, [
        selectedMedicationName,
        selectedRoute,
        selectedStrength,
        resolvedStrength.strength,
        strengthNotApplicable,
        medicationOptionConfirmed,
        selectedDose,
        customDose,
        showCustomDose,
        selectedFreq,
        customFreq,
        showCustomFreq,
        selectedNotes,
        customNote,
        selectedMdd,
        customMdd,
        showCustomMdd,
        selectedIcds,
        isComplete
    ]);

    // ── ICD handlers ──

    const toggleIcd = (code: string) => {
        setSelectedIcds((prev) => {
            const next = new Set(prev);
            if (next.has(code)) {
                next.delete(code);
            } else {
                next.add(code);
            }
            return next;
        });
    };

    const addIcd = (code: IcdCode) => {
        if (!allIcdCodes.some((c) => c.code === code.code)) {
            setAddedIcds((prev) => [...prev, code]);
        }
        setSelectedIcds((prev) => new Set([...prev, code.code]));
    };

    // ── Handlers ──

    const openSourceImages = async () => {
        // Which photo is showing and whether the magnifier is engaged now reset
        // inside SourcePhotoViewer, which owns that state for both card types.
        setImagesOpen(true);
        if (images.length > 0 || imagesLoading) {
            return;
        }
        setImagesLoading(true);
        setImagesError(null);
        try {
            const result = await fetchDraftImages(String(draft.visitId), String(draft.id));
            setImages(result.images);
            if (result.images.length === 0) {
                setImagesError("Those photos are no longer available.");
            }
        } catch {
            setImagesError("Could not load the photos. Please try again.");
        } finally {
            setImagesLoading(false);
        }
    };

    const handleSavePress = async () => {
        if (missingFields.length > 0) {
            // Reveal the card if the nurse folded it, otherwise the shake and the
            // red fields happen somewhere she cannot see.
            setCollapsed(false);
            setShowErrors(true);
            triggerShake();
            return;
        }
        setShowErrors(false);
        // Call parent to save (API call first), then animate out on success
        const success = await onSaved(String(draft.id));
        if (success) {
            triggerSlideOut(() => {});
        }
    };

    const handleDiscard = () => {
        showDialog(
            `Discard this ${isSupplement ? "supplement" : "medication"}?`,
            "This cannot be undone.",
            [
                { text: "Keep", style: "cancel" },
                {
                    text: "Discard",
                    style: "destructive",
                    onPress: () => triggerSlideOut(() => onDiscard())
                }
            ]
        );
    };

    // ── Chip select handlers ──

    const selectMedicationOption = (option: MedicationDraftProductSuggestion) => {
        setMedicationOptionConfirmed(true);
        setSelectedMedicationName(option.name);
        setSelectedRoute(option.route);
        setEnteredStrength("");
        setStrengthNotApplicable(false);
    };

    const selectDose = (dose: string) => {
        setSelectedDose((prev) => (prev === dose ? null : dose));
        setShowCustomDose(false);
        setCustomDose("");
    };

    const selectFreq = (freq: string) => {
        setSelectedFreq((prev) => (prev === freq ? null : freq));
        setShowCustomFreq(false);
        setCustomFreq("");
    };

    const toggleNote = (note: string) => {
        setSelectedNotes((prev) => {
            const next = new Set(prev);
            if (next.has(note)) {
                next.delete(note);
            } else {
                next.add(note);
            }
            return next;
        });
    };

    // Score badge removed per design — kept DRAFT badge only
    // ── Field rendering ──

    const renderField = (
        label: string,
        required: boolean,
        draftValue: string | null,
        suggestions: string[] | undefined,
        selectedValue: string | null,
        onSelect: (v: string) => void,
        showCustom: boolean,
        customValue: string,
        onCustomChange: (v: string) => void,
        onShowCustom: () => void,
        placeholder: string
    ) => {
        if (isSupplement) {
            return (
                <SuggestionQuestion
                    label={label === "DOSE" ? "Amount taken" : "Frequency"}
                    required={required}
                    value={selectedValue ?? (customValue || draftValue || "")}
                    onChange={(next) => {
                        onSelect(next);
                        onCustomChange(next);
                    }}
                    options={mergeStringOptions(draftValue, suggestions)}
                    loading={supplementSuggestions.loading}
                    showError={showErrors}
                    placeholder={placeholder}
                />
            );
        }
        const hasDraftValue = !!draftValue;
        const fieldOptions = mergeStringOptions(draftValue, selectedValue, suggestions);
        const hasSuggestions = fieldOptions.length > 0;
        const isLoading = enrichment.loading && !hasSuggestions;
        const noSuggestions =
            !enrichment.loading && (isSupplement || enrichment.source !== null) && !hasSuggestions;
        const failed = enrichment.failed && !hasDraftValue;
        const errored = showErrors && required && !hasDraftValue && !selectedValue && !customValue;
        const needsSuggestionChoice =
            required &&
            !hasDraftValue &&
            hasSuggestions &&
            !selectedValue &&
            !customValue &&
            !isLoading;

        return (
            <View style={ss.field}>
                <View style={ss.fieldLabelRow}>
                    <Text style={[ss.fieldLabel, errored && { color: colors.error500 }]}>
                        {label}
                    </Text>
                    {needsSuggestionChoice ? (
                        <Text style={ss.choiceHint}>
                            Could not extract. Please choose the right one.
                        </Text>
                    ) : !hasDraftValue && !isLoading && hasSuggestions ? (
                        <Text style={ss.aiLabel}>Suggested</Text>
                    ) : null}
                </View>

                {/* State 1: Loading */}
                {!hasDraftValue && isLoading && (
                    <View style={ss.loadingRow}>
                        <ActivityIndicator size="small" color={colors.teal400} />
                        <Text style={ss.loadingText}>Looking up...</Text>
                    </View>
                )}

                {/* State 2: Draft value and/or suggestions available */}
                {!isLoading && hasSuggestions && (
                    <>
                        <View style={ss.chipGroup}>
                            {fieldOptions.map((s) => (
                                <Pressable
                                    key={s}
                                    onPress={() => onSelect(s)}
                                    style={[
                                        ss.chip,
                                        selectedValue === s && !showCustom && ss.chipSelected
                                    ]}
                                >
                                    <Text
                                        style={[
                                            ss.chipText,
                                            selectedValue === s &&
                                                !showCustom &&
                                                ss.chipTextSelected
                                        ]}
                                    >
                                        {selectedValue === s && !showCustom ? "\u2713 " : ""}
                                        {s}
                                    </Text>
                                </Pressable>
                            ))}
                            <Pressable
                                onPress={onShowCustom}
                                style={[ss.chip, ss.chipOther, showCustom && ss.chipOtherSelected]}
                            >
                                <Text
                                    style={[
                                        ss.chipText,
                                        ss.chipOtherText,
                                        showCustom && ss.chipOtherTextSelected
                                    ]}
                                >
                                    Other
                                </Text>
                            </Pressable>
                        </View>
                        {showCustom && (
                            <TextInput
                                style={[
                                    ss.customInput,
                                    customValue
                                        ? {
                                              borderColor: colors.teal400,
                                              backgroundColor: colors.teal50
                                          }
                                        : {}
                                ]}
                                placeholder={placeholder}
                                placeholderTextColor={colors.textTertiary}
                                value={customValue}
                                onChangeText={onCustomChange}
                                autoFocus
                                autoCorrect={false}
                                spellCheck={false}
                            />
                        )}
                    </>
                )}

                {/* State 4: No suggestions / failed */}
                {!hasDraftValue && !isLoading && !hasSuggestions && (failed || noSuggestions) && (
                    <TextInput
                        style={ss.manualInput}
                        placeholder={placeholder}
                        placeholderTextColor={colors.textTertiary}
                        value={customValue}
                        onChangeText={onCustomChange}
                        autoCorrect={false}
                        spellCheck={false}
                    />
                )}

                {errored && (
                    <Text style={ss.fieldError}>Required — please select or enter a value</Text>
                )}
            </View>
        );
    };

    // ── Render ──

    return (
        // Outer view — JS-driver ONLY (layout props: marginBottom, scaleY collapse)
        <Animated.View
            style={{
                marginBottom: marginAnim,
                transform: [{ scaleY: heightAnim }]
            }}
        >
            {/* Inner view — native-driver-eligible (translateX + opacity) on iOS/Android; JS on web */}
            <Animated.View
                style={[
                    ss.card,
                    isComplete && ss.cardComplete,
                    showErrors && !isComplete && ss.cardError,
                    {
                        transform: [{ translateX: animatingOut ? slideAnim : shakeAnim }],
                        opacity: opacityAnim
                    }
                ]}
            >
                {/* Header — tinted by completeness, tap anywhere to fold the card */}
                <View
                    onLayout={(event) => {
                        const { width, height } = event.nativeEvent.layout;
                        setHeaderSize((previous) =>
                            previous.width === width && previous.height === height
                                ? previous
                                : { width, height }
                        );
                    }}
                    style={[
                        ss.headerTint,
                        isComplete ? ss.headerTintComplete : ss.headerTintMissing,
                        collapsed && ss.headerTintCollapsed
                    ]}
                >
                    {awaitingDiagnoses && headerSize.width > 0 && (
                        <ProcessingFade width={headerSize.width} height={headerSize.height} />
                    )}
                    <View style={ss.header}>
                        {/* Status dot. Green + tick when saveable; amber + "!"
                                when not, and pressing it names what is missing
                                rather than making her hunt the red fields. */}
                        <View
                            accessibilityRole="image"
                            accessibilityLabel={
                                awaitingDiagnoses
                                    ? "Diagnosis codes still loading"
                                    : isComplete
                                      ? "Ready to save"
                                      : `Please fill in ${missingFields.join(", ")}`
                            }
                            style={[
                                ss.statusDot,
                                awaitingDiagnoses
                                    ? ss.statusDotProcessing
                                    : isComplete
                                      ? ss.statusDotComplete
                                      : ss.statusDotMissing
                            ]}
                        >
                            {awaitingDiagnoses ? (
                                <ActivityIndicator size="small" color={colors.gray600} />
                            ) : (
                                <MaterialCommunityIcons
                                    name={isComplete ? "check-bold" : "exclamation"}
                                    size={13}
                                    color="#FFFFFF"
                                />
                            )}
                        </View>
                        <Pressable
                            onPress={() => setCollapsed((value) => !value)}
                            style={ss.headerLeft}
                            accessibilityRole="button"
                            accessibilityState={{ expanded: !collapsed }}
                            accessibilityLabel={`${headerTitle}, ${
                                isComplete ? "complete" : `missing ${missingFields.join(", ")}`
                            }. Tap to ${collapsed ? "expand" : "collapse"}.`}
                        >
                            <View style={ss.nameRow}>
                                <Text style={ss.name}>{headerTitle}</Text>
                                {selectedRoute && (
                                    <View style={ss.routeBadge}>
                                        <Text style={ss.routeText}>{selectedRoute}</Text>
                                    </View>
                                )}
                                {isSupplement && (
                                    <View style={ss.supplementBadge}>
                                        <Text style={ss.supplementBadgeText}>SUPPLEMENT</Text>
                                    </View>
                                )}
                            </View>
                            {/* Collapsed cards show the shortfall inline, so a folded
                                list still says which cards need work. */}
                            {/* Always visible, folded or not: what she has to do, then
                                what the server is still doing. No tap needed for
                                either — a card should not hide its own state. */}
                            {!isComplete && missingFields.length > 0 && (
                                <Text style={ss.headerMissingSummary}>
                                    Please fill in {missingFields.join(", ")}
                                </Text>
                            )}
                            {awaitingDiagnoses && (
                                <Text style={ss.headerProcessingText}>
                                    Processing diagnoses&hellip;
                                </Text>
                            )}
                        </Pressable>
                        <View style={ss.badges}>
                            {sourceImageIndexes.length > 0 && (
                                <SourcePhotoButton
                                    onPress={openSourceImages}
                                    count={sourceImageIndexes.length}
                                    pending={awaitingImageLinks}
                                    accessibilityLabel={
                                        awaitingImageLinks
                                            ? "Still working out which photos this medication was read from"
                                            : `Show the ${
                                                  sourceImageIndexes.length === 1
                                                      ? "photo"
                                                      : `${sourceImageIndexes.length} photos`
                                              } this medication was read from`
                                    }
                                />
                            )}
                            {draft.origin === "patient_photo" && (
                                // Read off a photo the PATIENT sent before the
                                // visit, not one the nurse took — worth knowing
                                // before trusting the strength on the card.
                                <View style={ss.patientPhotoBadge}>
                                    <Text style={ss.patientPhotoBadgeText}>PATIENT PHOTO</Text>
                                </View>
                            )}
                            <View style={ss.draftBadge}>
                                <Text style={ss.draftBadgeText}>DRAFT</Text>
                            </View>
                            <Pressable
                                onPress={() => setCollapsed((value) => !value)}
                                hitSlop={8}
                                accessibilityRole="button"
                                accessibilityLabel={collapsed ? "Expand card" : "Collapse card"}
                            >
                                <MaterialCommunityIcons
                                    name={collapsed ? "chevron-down" : "chevron-up"}
                                    size={22}
                                    color={colors.textTertiary}
                                />
                            </Pressable>
                        </View>
                    </View>
                </View>

                {/* Body — hidden while folded. Default is open: a nurse should see
                    the whole card without a tap, and folding is for getting a
                    finished medication out of her way. */}
                {!collapsed && (
                    <>
                        {cardIsEnriching && (
                            <View style={ss.enrichingStrip} pointerEvents="none">
                                <ActivityIndicator size="small" color={colors.teal600} />
                                <Text style={ss.enrichingStripText}>
                                    Looking up dose, frequency, notes, and diagnosis
                                </Text>
                            </View>
                        )}

                        {(productOptions.length > 1 || needsMedicationConfirmation) && (
                            <View style={ss.field}>
                                <View style={ss.fieldLabelRow}>
                                    <Text style={ss.fieldLabel}>STRENGTH OPTIONS</Text>
                                    {needsMedicationConfirmation && !medicationOptionConfirmed && (
                                        <Text style={ss.choiceHint}>
                                            Please choose the right option.
                                        </Text>
                                    )}
                                </View>
                                <View style={ss.chipGroup}>
                                    {productOptions.map((option) => {
                                        const selected =
                                            medicationOptionConfirmed &&
                                            option.name === selectedMedicationName;
                                        const routeSuffix =
                                            option.route && option.route !== selectedRoute
                                                ? ` · ${option.route}`
                                                : "";
                                        return (
                                            <Pressable
                                                key={`${option.name}-${option.route}`}
                                                onPress={() => selectMedicationOption(option)}
                                                style={[ss.chip, selected && ss.chipSelected]}
                                            >
                                                <Text
                                                    style={[
                                                        ss.chipText,
                                                        selected && ss.chipTextSelected
                                                    ]}
                                                >
                                                    {selected ? "\u2713 " : ""}
                                                    {option.label}
                                                    {routeSuffix}
                                                </Text>
                                            </Pressable>
                                        );
                                    })}
                                </View>
                            </View>
                        )}

                        {resolvedStrength.needsEntry && (
                            <StrengthQuestion
                                visitId={draft.visitId}
                                productName={selectedMedicationName}
                                value={enteredStrength}
                                onChange={setEnteredStrength}
                                notApplicable={strengthNotApplicable}
                                onNotApplicableChange={setStrengthNotApplicable}
                                showError={showErrors}
                            />
                        )}
                        {isSupplement && (
                            <SuggestionQuestion
                                label="Route"
                                required
                                value={selectedRoute ?? ""}
                                onChange={setSelectedRoute}
                                options={SUPPLEMENT_ROUTES}
                                showError={showErrors}
                                placeholder="Enter route"
                            />
                        )}

                        {/* Dose — always the per-administration amount */}
                        {renderField(
                            "DOSE",
                            true,
                            extractedDraftDose,
                            doseSuggestions,
                            selectedDose,
                            selectDose,
                            showCustomDose,
                            customDose,
                            setCustomDose,
                            () => {
                                setShowCustomDose(true);
                                setSelectedDose(null);
                            },
                            "Enter dose (e.g. 1 tablet)"
                        )}

                        {/* Frequency */}
                        {renderField(
                            "FREQUENCY",
                            true,
                            extractedDraftFrequency,
                            frequencySuggestions,
                            selectedFreq,
                            selectFreq,
                            showCustomFreq,
                            customFreq,
                            setCustomFreq,
                            () => {
                                setShowCustomFreq(true);
                                setSelectedFreq(null);
                            },
                            "Enter frequency"
                        )}

                        {/* Max daily dose — its own field, only for PRN. The
                            source-stated ceiling comes first (preselected when
                            extracted); the schedule-derived value follows. */}
                        {askMdd && !isSupplement && (
                            <View style={ss.field}>
                                <View style={ss.fieldLabelRow}>
                                    <Text style={ss.fieldLabel}>MAX DAILY DOSE</Text>
                                    {!effectiveMdd && (
                                        <Text style={ss.choiceHint}>
                                            Taken as needed — a daily ceiling is required.
                                        </Text>
                                    )}
                                </View>
                                <View style={ss.chipGroup}>
                                    {mddChipOptions.map((opt) => (
                                        <TouchableOpacity
                                            key={opt}
                                            style={[
                                                ss.chip,
                                                selectedMdd === opt &&
                                                    !showCustomMdd &&
                                                    ss.chipSelected
                                            ]}
                                            onPress={() => {
                                                setSelectedMdd(
                                                    !showCustomMdd && selectedMdd === opt
                                                        ? null
                                                        : opt
                                                );
                                                setShowCustomMdd(false);
                                            }}
                                        >
                                            <Text
                                                style={[
                                                    ss.chipText,
                                                    selectedMdd === opt &&
                                                        !showCustomMdd &&
                                                        ss.chipTextSelected
                                                ]}
                                            >
                                                {opt}
                                                {mddExplainByValue.has(opt)
                                                    ? ` (${mddExplainByValue.get(opt)})`
                                                    : ""}
                                            </Text>
                                        </TouchableOpacity>
                                    ))}
                                    <TouchableOpacity
                                        style={[
                                            ss.chip,
                                            ss.chipOther,
                                            showCustomMdd && ss.chipOtherSelected
                                        ]}
                                        onPress={() => {
                                            setShowCustomMdd(true);
                                            setSelectedMdd(null);
                                        }}
                                    >
                                        <Text
                                            style={[
                                                ss.chipOtherText,
                                                showCustomMdd && ss.chipOtherTextSelected
                                            ]}
                                        >
                                            Other
                                        </Text>
                                    </TouchableOpacity>
                                </View>
                                {showCustomMdd && (
                                    <TextInput
                                        style={ss.customInput}
                                        placeholder="e.g., 8 tablets"
                                        placeholderTextColor="#B0AECE"
                                        value={customMdd}
                                        onChangeText={setCustomMdd}
                                        autoFocus
                                        autoCorrect={false}
                                        spellCheck={false}
                                    />
                                )}
                                <Text style={ss.mddHint}>
                                    The most the patient may take in 24 hours.
                                </Text>
                            </View>
                        )}

                        {/* Admin Notes — only show if draft has notes OR suggestions exist */}
                        {(extractedDraftAdministrationNotes || adminNoteOptions.length > 0) && (
                            <View style={ss.field}>
                                <View style={ss.fieldLabelRow}>
                                    <Text style={ss.fieldLabel}>NOTES</Text>
                                    {needsAdminNoteChoice ? (
                                        <Text style={ss.choiceHint}>
                                            Could not extract. Please choose the right one.
                                        </Text>
                                    ) : !extractedDraftAdministrationNotes &&
                                      !enrichment.loading ? (
                                        <Text style={ss.aiLabel}>Suggested</Text>
                                    ) : null}
                                </View>
                                {enrichment.loading && adminNoteOptions.length === 0 ? (
                                    <View style={ss.loadingRow}>
                                        <ActivityIndicator size="small" color={colors.teal400} />
                                        <Text style={ss.loadingText}>Looking up...</Text>
                                    </View>
                                ) : (
                                    <>
                                        <View style={ss.chipGroup}>
                                            {adminNoteOptions.map((note) => (
                                                <Pressable
                                                    key={note}
                                                    onPress={() => toggleNote(note)}
                                                    style={[
                                                        ss.chip,
                                                        selectedNotes.has(note) && ss.chipSelected
                                                    ]}
                                                >
                                                    <Text
                                                        style={[
                                                            ss.chipText,
                                                            selectedNotes.has(note) &&
                                                                ss.chipTextSelected
                                                        ]}
                                                    >
                                                        {selectedNotes.has(note) ? "\u2713 " : ""}
                                                        {note}
                                                    </Text>
                                                </Pressable>
                                            ))}
                                            <Pressable
                                                onPress={() => setShowCustomNote(!showCustomNote)}
                                                style={[
                                                    ss.chip,
                                                    ss.chipOther,
                                                    showCustomNote && ss.chipOtherSelected
                                                ]}
                                            >
                                                <Text
                                                    style={[
                                                        ss.chipText,
                                                        ss.chipOtherText,
                                                        showCustomNote && ss.chipOtherTextSelected
                                                    ]}
                                                >
                                                    Other
                                                </Text>
                                            </Pressable>
                                        </View>
                                        {showCustomNote && (
                                            <TextInput
                                                style={[
                                                    ss.customInput,
                                                    customNote
                                                        ? {
                                                              borderColor: colors.teal400,
                                                              backgroundColor: colors.teal50
                                                          }
                                                        : {}
                                                ]}
                                                placeholder="Enter custom note..."
                                                placeholderTextColor={colors.textTertiary}
                                                value={customNote}
                                                onChangeText={setCustomNote}
                                                autoFocus
                                                autoCorrect={false}
                                                spellCheck={false}
                                            />
                                        )}
                                    </>
                                )}
                            </View>
                        )}

                        {/* ICD Codes — show for all cards that have extraction or suggestion ICD codes */}
                        {!isSupplement || allIcdCodes.length > 0 ? (
                            <View
                                style={[
                                    ss.field,
                                    showErrors &&
                                        !isSupplement &&
                                        selectedIcds.size === 0 &&
                                        ss.icdErrorContainer
                                ]}
                            >
                                <View style={ss.fieldLabelRow}>
                                    <Text
                                        style={[
                                            ss.fieldLabel,
                                            showErrors &&
                                                !isSupplement &&
                                                selectedIcds.size === 0 && {
                                                    color: colors.error500
                                                }
                                        ]}
                                    >
                                        DIAGNOSIS CODES
                                    </Text>
                                    {!isSupplement &&
                                        allIcdCodes.length > 0 &&
                                        selectedIcds.size === 0 &&
                                        !enrichment.loading && (
                                            <Text style={ss.choiceHint}>
                                                Could not extract. Please choose the right one.
                                            </Text>
                                        )}
                                </View>
                                {showErrors && !isSupplement && selectedIcds.size === 0 && (
                                    <Text style={ss.fieldError}>
                                        Select at least one diagnosis code
                                    </Text>
                                )}

                                {enrichment.loading && allIcdCodes.length === 0 ? (
                                    <View style={ss.loadingRow}>
                                        <ActivityIndicator size="small" color={colors.teal400} />
                                        <Text style={ss.loadingText}>Looking up...</Text>
                                    </View>
                                ) : (
                                    <>
                                        <IcdChipPicker
                                            suggestedCodes={visibleIcdCodes}
                                            selectedCodes={selectedIcds}
                                            onToggle={toggleIcd}
                                            onAdd={addIcd}
                                        />
                                    </>
                                )}
                            </View>
                        ) : null}

                        {/* Merge notes */}

                        {/* Actions */}
                        <View style={ss.actions}>
                            <TouchableOpacity
                                style={ss.btnDiscard}
                                onPress={handleDiscard}
                                activeOpacity={0.7}
                            >
                                <Text style={ss.btnDiscardText}>Discard</Text>
                            </TouchableOpacity>
                            <TouchableOpacity
                                style={[
                                    ss.btnSave,
                                    isComplete && !cardIsEnriching
                                        ? ss.btnSaveActive
                                        : ss.btnSaveDisabled
                                ]}
                                onPress={handleSavePress}
                                disabled={externalSaving || cardIsEnriching}
                                activeOpacity={0.8}
                            >
                                {externalSaving ? (
                                    <ActivityIndicator size="small" color="#FFFFFF" />
                                ) : (
                                    <Text
                                        style={[
                                            ss.btnSaveText,
                                            !isComplete && ss.btnSaveTextDisabled
                                        ]}
                                    >
                                        {cardIsEnriching
                                            ? "Enriching..."
                                            : `Save ${isSupplement ? "Supplement" : "Medication"}`}
                                    </Text>
                                )}
                            </TouchableOpacity>
                        </View>
                    </>
                )}

                <SourcePhotoViewer
                    visible={imagesOpen}
                    onClose={() => setImagesOpen(false)}
                    title={headerTitle}
                    images={images}
                    loading={imagesLoading}
                    error={imagesError}
                />

                <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />
            </Animated.View>
        </Animated.View>
    );
}

// ── Styles ──

const ss = StyleSheet.create({
    card: {
        backgroundColor: colors.surface,
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: 18,
        padding: 18,
        marginBottom: 14,
        overflow: "hidden",
        position: "relative",
        ...shadows.sm
    },
    cardComplete: {
        borderColor: colors.teal400,
        ...shadows.md
    },
    cardError: {
        borderColor: colors.error500
    },

    // Header tint — bleeds to the card edges over the card's own padding, so the
    // status colour reads as the top of the card rather than a stripe inside it.
    headerTint: {
        marginTop: -18,
        marginHorizontal: -18,
        paddingTop: 14,
        paddingHorizontal: 18,
        paddingBottom: 10,
        marginBottom: 12,
        borderTopLeftRadius: 16,
        borderTopRightRadius: 16,
        borderBottomWidth: 1
    },
    headerTintComplete: {
        backgroundColor: colors.success50,
        borderBottomColor: colors.success400
    },
    headerTintMissing: {
        backgroundColor: colors.warning50,
        borderBottomColor: colors.warning400
    },
    // Folded: no gap below, the tint IS the card.
    // The halftone sits behind the header content and must not intercept taps.
    processingFade: {
        position: "absolute",
        left: 0,
        right: 0,
        top: 0
    },
    headerTintCollapsed: {
        marginBottom: -18,
        borderBottomLeftRadius: 16,
        borderBottomRightRadius: 16,
        borderBottomWidth: 0
    },
    statusDot: {
        width: 20,
        height: 20,
        borderRadius: 10,
        alignItems: "center",
        justifyContent: "center",
        marginRight: 8,
        marginTop: 1,
        flexShrink: 0
    },
    // No filled circle behind the spinner — a coloured disc under a grey
    // indicator reads as a third status rather than as "working".
    statusDotProcessing: {
        backgroundColor: "transparent"
    },
    statusDotComplete: {
        backgroundColor: colors.success600
    },
    statusDotMissing: {
        backgroundColor: colors.warning600
    },
    headerProcessingText: {
        marginTop: 3,
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.gray600
    },
    headerMissingSummary: {
        marginTop: 4,
        fontSize: 12,
        lineHeight: 16,
        fontFamily: fonts.semiBold,
        color: colors.warning600
    },

    // Header
    header: {
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "flex-start",
        marginBottom: 8
    },
    headerLeft: {
        flex: 1
    },
    nameRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        gap: 6
    },
    name: {
        fontSize: 16,
        fontFamily: fonts.extraBold,
        color: colors.textPrimary
    },
    badges: {
        flexDirection: "row",
        gap: 6,
        alignItems: "center",
        flexShrink: 0,
        marginLeft: 8
    },
    scoreBadge: {
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6
    },
    scoreBadgeText: {
        fontSize: 10,
        fontFamily: fonts.bold
    },
    draftBadge: {
        backgroundColor: colors.teal50,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6
    },
    draftBadgeText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.teal600,
        textTransform: "uppercase",
        letterSpacing: 0.5
    },
    patientPhotoBadge: {
        backgroundColor: colors.pink50,
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 6
    },
    patientPhotoBadgeText: {
        fontSize: 10,
        fontFamily: fonts.bold,
        color: colors.pink600,
        textTransform: "uppercase",
        letterSpacing: 0.5
    },
    supplementBadge: {
        alignSelf: "flex-start",
        backgroundColor: "#FFFBEB",
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: radius.full
    },
    supplementBadgeText: {
        fontSize: 9,
        fontFamily: fonts.bold,
        color: "#B45309",
        textTransform: "uppercase",
        letterSpacing: 0.5
    },

    // Route
    routeBadge: {
        backgroundColor: "#EDE9FE",
        paddingHorizontal: 8,
        paddingVertical: 2,
        borderRadius: 6,
        alignSelf: "flex-start"
    },
    routeText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: "#7C3AED"
    },

    // Fields
    mddHint: { fontSize: 11, fontFamily: fonts.regular, color: "#B45309", marginTop: 6 },
    field: {
        marginBottom: 10
    },
    fieldLabelRow: {
        flexDirection: "row",
        alignItems: "center",
        flexWrap: "wrap",
        marginBottom: 3
    },
    fieldLabel: {
        fontSize: 10,
        fontFamily: fonts.bold,
        textTransform: "uppercase",
        letterSpacing: 0.8,
        color: colors.textTertiary
    },
    fieldSublabel: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        marginBottom: 6
    },
    fieldValue: {
        fontSize: 14,
        fontFamily: fonts.medium,
        color: colors.textPrimary,
        lineHeight: 20
    },
    fieldError: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: colors.error500,
        marginTop: 4
    },

    // Loading state
    loadingRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: 6,
        paddingVertical: 3
    },
    loadingText: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: colors.textTertiary
    },
    enrichingStrip: {
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        marginTop: 10,
        marginBottom: 8,
        paddingHorizontal: 12,
        paddingVertical: 9,
        borderRadius: 10,
        borderWidth: 1,
        borderColor: "#B8F3E8",
        backgroundColor: "#F2FFFC"
    },
    enrichingStripText: {
        flexShrink: 1,
        fontSize: 11,
        lineHeight: 15,
        fontFamily: fonts.semiBold,
        color: colors.teal700
    },

    // AI label
    aiLabel: {
        fontSize: 10,
        fontFamily: fonts.semiBold,
        color: colors.textTertiary,
        marginLeft: 6
    },
    choiceHint: {
        fontSize: 10,
        fontFamily: fonts.semiBold,
        color: colors.error500,
        marginLeft: 6
    },

    // Chips — match prototype exactly: 5px 11px padding, borderRadius 9999, 11px font
    chipGroup: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: 5,
        marginTop: 4
    },
    chip: {
        paddingHorizontal: 11,
        paddingVertical: 5,
        borderRadius: 9999,
        borderWidth: 1.5,
        borderColor: "#EDEAF2",
        backgroundColor: "#FFFFFF"
    },
    chipSelected: {
        borderColor: "#45D2B0",
        backgroundColor: "#45D2B0"
    },
    // ICD chip variant — light teal unselected
    chipIcd: {
        borderColor: "#CCFBEF",
        backgroundColor: "#F0FDF9"
    },
    chipIcdText: {
        color: "#14957D"
    },
    chipText: {
        fontSize: 11,
        fontFamily: fonts.semiBold,
        color: "#4A4A68"
    },
    chipTextSelected: {
        color: "#FFFFFF"
    },
    chipOther: {
        borderStyle: "dashed" as "solid",
        borderColor: "#DDD8E6"
    },
    chipOtherSelected: {
        borderColor: "#E748F5",
        backgroundColor: "#E748F5",
        borderStyle: "solid" as const
    },
    chipOtherText: {
        color: "#8B89A8"
    },
    chipOtherTextSelected: {
        color: "#FFFFFF"
    },
    chipInputText: {
        fontSize: 11,
        fontFamily: fonts.medium,
        color: "#8B89A8",
        padding: 0,
        minWidth: 60
    },

    // Custom input
    customInput: {
        marginTop: 8,
        borderWidth: 1.5,
        borderColor: colors.border,
        borderRadius: radius.md,
        padding: spacing.sm,
        fontSize: 13,
        fontFamily: fonts.regular,
        color: colors.textPrimary,
        backgroundColor: colors.surface
    },
    manualInput: {
        borderWidth: 1.5,
        borderColor: colors.gray300,
        borderStyle: "dashed" as "solid",
        borderRadius: radius.md,
        padding: spacing.sm,
        fontSize: 12,
        fontFamily: fonts.medium,
        color: colors.textTertiary,
        backgroundColor: colors.surface
    },

    // ICD error container
    icdErrorContainer: {
        padding: 8,
        borderRadius: 12,
        borderWidth: 1.5,
        borderColor: colors.error500,
        backgroundColor: colors.error50
    },

    // Merge notes
    mergeNotes: {
        fontSize: 11,
        fontFamily: fonts.regular,
        fontStyle: "italic",
        color: colors.textTertiary,
        marginBottom: 8
    },

    // Actions
    actions: {
        flexDirection: "row",
        gap: 8,
        marginTop: 14,
        paddingTop: 14,
        borderTopWidth: 1,
        borderTopColor: colors.border
    },
    btnDiscard: {
        paddingHorizontal: 12,
        height: 38,
        borderRadius: radius.full,
        borderWidth: 1.5,
        borderColor: "#FECACA",
        backgroundColor: colors.error50,
        justifyContent: "center",
        alignItems: "center"
    },
    btnDiscardText: {
        fontSize: 11,
        fontFamily: fonts.bold,
        color: colors.error500
    },
    btnSave: {
        flex: 1,
        height: 38,
        borderRadius: radius.full,
        justifyContent: "center",
        alignItems: "center"
    },
    btnSaveActive: {
        backgroundColor: colors.teal500
    },
    btnSaveDisabled: {
        backgroundColor: colors.gray200
    },
    btnSaveText: {
        fontSize: 13,
        fontFamily: fonts.bold,
        color: "#FFFFFF"
    },
    btnSaveTextDisabled: {
        color: colors.textTertiary
    }
});
